import { Clock, CheckCircle } from "lucide-react";
import { formatDateMoscow } from "../../utils/formatters";
import AnimatedNumber from "../AnimatedNumber";

const StatusPill = ({ status }) => {
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1.5 bg-[#3B82F6]/12 text-[#60A5FA] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
        <Clock size={12} /> В работе
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="inline-flex items-center gap-1.5 bg-white/12 text-[#EDEDED] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
        <Clock size={12} /> В ожидании
      </span>
    );
  }
  // closed / completed — neutral gray pill with a green check
  return (
    <span className="inline-flex items-center gap-1.5 bg-[#A1A1AA]/12 text-[#A1A1AA] px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap">
      <CheckCircle size={12} className="text-[#34D399]" /> Выполнено
    </span>
  );
};

const pnlClasses = (positive) =>
  positive ? "text-[#34D399] pnl-glow-up" : "text-[#F87171] pnl-glow-down";

const Field = ({ label, children, align = "left" }) => (
  <div className={align === "right" ? "text-right" : ""}>
    <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-0.5">{label}</div>
    <div className="font-mono text-sm">{children}</div>
  </div>
);

const TradesTable = ({ trades }) => {
  if (!trades || trades.length === 0) {
    return (
      <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] py-14 text-center text-[#71717A]">
        Нет активных сделок
      </div>
    );
  }

  return (
    <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden">
      {/* ===== Desktop table ===== */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full" data-testid="trades-table">
          <thead>
            <tr className="border-b border-[#1F1F1F]">
              {["Название", "Вклад / Доход", "PnL", "Статус", "Дата закрытия"].map((h) => (
                <th key={h} className="text-left text-[#71717A] text-[11px] uppercase tracking-wider font-medium py-3.5 px-5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.map((trade) => {
              const currentPnl = trade.current_pnl_percent || 0;
              const currentIncome = trade.current_income || 0;
              const isPositive = currentPnl >= 0;
              return (
                <tr key={trade.id} className="border-b border-[#1F1F1F]/60 last:border-0 hover:bg-white/[0.02] transition-colors">
                  <td className="py-4 px-5">
                    <span className="text-white font-semibold">{trade.asset_name}</span>
                  </td>
                  <td className="py-4 px-5">
                    <div className="font-mono text-sm">
                      <span className="text-white">${trade.investment?.toFixed(2)}</span>
                      <span className="text-[#52525B] mx-1.5">/</span>
                      <AnimatedNumber value={currentIncome} prefix="$" showSign flash className={pnlClasses(isPositive)} />
                    </div>
                  </td>
                  <td className="py-4 px-5">
                    <AnimatedNumber value={currentPnl} suffix="%" showSign flash className={`font-mono text-sm font-semibold ${pnlClasses(isPositive)}`} />
                  </td>
                  <td className="py-4 px-5"><StatusPill status={trade.status} /></td>
                  <td className="py-4 px-5 text-[#A1A1AA] font-mono text-xs">{formatDateMoscow(trade.close_date)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ===== Mobile cards ===== */}
      <div className="lg:hidden divide-y divide-[#1F1F1F]">
        {trades.map((trade) => {
          const currentPnl = trade.current_pnl_percent || 0;
          const currentIncome = trade.current_income || 0;
          const isPositive = currentPnl >= 0;
          return (
            <div key={trade.id} className="p-4" data-testid={`trade-card-${trade.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-semibold text-[15px]">{trade.asset_name}</span>
                <StatusPill status={trade.status} />
              </div>
              <div className="grid grid-cols-2 gap-y-3">
                <Field label="Вклад"><span className="text-white">${trade.investment?.toFixed(2)}</span></Field>
                <Field label="Доход" align="right"><AnimatedNumber value={currentIncome} prefix="$" showSign flash className={pnlClasses(isPositive)} /></Field>
                <Field label="PnL"><AnimatedNumber value={currentPnl} suffix="%" showSign flash className={`font-semibold ${pnlClasses(isPositive)}`} /></Field>
                <Field label="Закрытие" align="right"><span className="text-[#A1A1AA] text-xs">{formatDateMoscow(trade.close_date)}</span></Field>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TradesTable;
